import { useState, useRef, useEffect } from 'react'
import {
  FileText,
  Image as ImageIcon,
  DownloadSimple,
  File,
  FilePdf,
  Play,
  Pause,
  Trash,
  Prohibit,
  Eye
} from '@phosphor-icons/react'
import type { ChatMessage, ChatAttachment } from '../../services/chat'
import { DocumentViewer } from '../DocumentViewer'

function getInitials(name: string): string {
  return name
    .split(' ')
    .map(word => word[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

interface MessageBubbleProps {
  message: ChatMessage
  onDelete?: (messageId: string) => void
  deleting?: boolean
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

function formatTime(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit'
  })
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  if (date.toDateString() === today.toDateString()) {
    return 'Hoje'
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return 'Ontem'
  }
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short'
  })
}

function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) {
    return <ImageIcon size={24} className="text-blue-500" />
  }
  if (mimeType === 'application/pdf') {
    return <FilePdf size={24} className="text-red-500" />
  }
  if (mimeType.includes('word') || mimeType.includes('document')) {
    return <FileText size={24} className="text-blue-600" />
  }
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) {
    return <FileText size={24} className="text-green-600" />
  }
  return <File size={24} className="text-gray-500" />
}

function getDeletedMessageType(message: ChatMessage): string {
  if (message.attachment) {
    if (message.attachment.mimeType.startsWith('audio/')) {
      return 'audio'
    }
    if (message.attachment.mimeType.startsWith('image/')) {
      return 'imagem'
    }
    return 'anexo'
  }
  return 'mensagem'
}

// Custom audio player with speed control
function AudioPlayer({ url }: { url: string }) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [playbackRate, setPlaybackRate] = useState(1)

  const speeds = [1, 1.5, 2]

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause()
      } else {
        audioRef.current.play()
      }
      setIsPlaying(!isPlaying)
    }
  }

  const cycleSpeed = () => {
    const currentIndex = speeds.indexOf(playbackRate)
    const nextIndex = (currentIndex + 1) % speeds.length
    const newRate = speeds[nextIndex]
    setPlaybackRate(newRate)
    if (audioRef.current) {
      audioRef.current.playbackRate = newRate
    }
  }

  const formatAudioTime = (time: number) => {
    const mins = Math.floor(time / 60)
    const secs = Math.floor(time % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime)
    }
  }

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration)
    }
  }

  const handleEnded = () => {
    setIsPlaying(false)
    setCurrentTime(0)
    if (audioRef.current) {
      audioRef.current.currentTime = 0
    }
  }

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value)
    setCurrentTime(newTime)
    if (audioRef.current) {
      audioRef.current.currentTime = newTime
    }
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  return (
    <div className="mt-2 p-3 bg-white/50 rounded-lg min-w-[200px]">
      <audio
        ref={audioRef}
        src={url}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
      />
      <div className="flex items-center gap-3">
        {/* Play/Pause button */}
        <button
          onClick={togglePlay}
          className="w-10 h-10 flex items-center justify-center bg-emerald-500 text-white rounded-full hover:bg-emerald-600 transition-colors flex-shrink-0"
        >
          {isPlaying ? <Pause size={20} weight="fill" /> : <Play size={20} weight="fill" />}
        </button>

        {/* Waveform / Progress */}
        <div className="flex-1 flex flex-col gap-1">
          <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="absolute left-0 top-0 h-full bg-emerald-500 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
            <input
              type="range"
              min={0}
              max={duration || 100}
              value={currentTime}
              onChange={handleSeek}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>{formatAudioTime(currentTime)}</span>
            <span>{formatAudioTime(duration)}</span>
          </div>
        </div>

        {/* Speed control */}
        <button
          onClick={cycleSpeed}
          className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors flex-shrink-0"
          title="Velocidade de reproducao"
        >
          {playbackRate}x
        </button>
      </div>
    </div>
  )
}

function canPreview(mimeType: string): boolean {
  return (
    mimeType === 'application/pdf' ||
    mimeType.startsWith('image/') ||
    mimeType.includes('word') ||
    mimeType.includes('document') ||
    mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  )
}

function AttachmentPreview({ attachment }: { attachment: ChatAttachment }) {
  const [showViewer, setShowViewer] = useState(false)
  const isImage = attachment.mimeType.startsWith('image/')
  const isAudio = attachment.mimeType.startsWith('audio/')
  const isPreviewable = canPreview(attachment.mimeType)

  if (isImage) {
    return (
      <>
        <button
          onClick={() => setShowViewer(true)}
          className="block mt-2 max-w-xs cursor-pointer"
        >
          <img
            src={attachment.url}
            alt={attachment.name}
            className="rounded-lg max-h-48 object-cover hover:opacity-90 transition-opacity"
            loading="lazy"
          />
        </button>
        <DocumentViewer
          isOpen={showViewer}
          onClose={() => setShowViewer(false)}
          url={attachment.url}
          fileName={attachment.name}
          mimeType={attachment.mimeType}
        />
      </>
    )
  }

  if (isAudio) {
    return <AudioPlayer url={attachment.url} />
  }

  // For PDFs, DOCXs and other documents
  return (
    <>
      <div className="flex items-center gap-3 mt-2 p-3 bg-white/50 rounded-lg">
        {getFileIcon(attachment.mimeType)}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate text-gray-700">{attachment.name}</p>
          <p className="text-xs text-gray-500">{formatFileSize(attachment.size)}</p>
        </div>
        <div className="flex items-center gap-1">
          {isPreviewable && (
            <button
              onClick={() => setShowViewer(true)}
              className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
              title="Visualizar"
            >
              <Eye size={18} />
            </button>
          )}
          <a
            href={attachment.url}
            download={attachment.name}
            className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors"
            title="Baixar"
          >
            <DownloadSimple size={18} />
          </a>
        </div>
      </div>
      {isPreviewable && (
        <DocumentViewer
          isOpen={showViewer}
          onClose={() => setShowViewer(false)}
          url={attachment.url}
          fileName={attachment.name}
          mimeType={attachment.mimeType}
        />
      )}
    </>
  )
}

// Confirmation Modal Component
function DeleteConfirmationModal({
  isOpen,
  onConfirm,
  onCancel,
  deleting
}: {
  isOpen: boolean
  onConfirm: () => void
  onCancel: () => void
  deleting?: boolean
}) {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onCancel}>
      <div
        className="bg-white rounded-xl p-6 max-w-sm mx-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
            <Trash size={20} className="text-red-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Apagar mensagem?</h3>
            <p className="text-sm text-gray-500">Esta acao nao pode ser desfeita</p>
          </div>
        </div>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            disabled={deleting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
          >
            {deleting ? 'Apagando...' : 'Apagar'}
          </button>
        </div>
      </div>
    </div>
  )
}

export function MessageBubble({ message, onDelete, deleting }: MessageBubbleProps) {
  const isOwn = message.isOwnMessage
  const isDeleted = !!message.deletedAt
  const senderLabel = message.senderCompanyType === 'shipper' ? 'Embarcador' : 'Agente'
  const [showContextMenu, setShowContextMenu] = useState(false)
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 })
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const longPressTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const bubbleRef = useRef<HTMLDivElement>(null)

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (bubbleRef.current && !bubbleRef.current.contains(e.target as Node)) {
        setShowContextMenu(false)
      }
    }
    if (showContextMenu) {
      document.addEventListener('click', handleClickOutside)
    }
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [showContextMenu])

  // Handle right-click (desktop)
  const handleContextMenu = (e: React.MouseEvent) => {
    if (!isOwn || !onDelete || isDeleted) return
    e.preventDefault()
    setContextMenuPosition({ x: e.clientX, y: e.clientY })
    setShowContextMenu(true)
  }

  // Handle long press (mobile)
  const handleTouchStart = () => {
    if (!isOwn || !onDelete || isDeleted) return
    longPressTimeoutRef.current = setTimeout(() => {
      setShowContextMenu(true)
    }, 500)
  }

  const handleTouchEnd = () => {
    if (longPressTimeoutRef.current) {
      clearTimeout(longPressTimeoutRef.current)
      longPressTimeoutRef.current = null
    }
  }

  const handleDeleteClick = () => {
    setShowContextMenu(false)
    setShowDeleteConfirm(true)
  }

  const handleConfirmDelete = () => {
    if (onDelete) {
      onDelete(message.id)
    }
    setShowDeleteConfirm(false)
  }

  // Avatar component
  const Avatar = () => {
    if (message.senderAvatarUrl) {
      return (
        <img
          src={message.senderAvatarUrl}
          alt={message.senderName}
          className="w-8 h-8 rounded-full object-cover flex-shrink-0"
        />
      )
    }
    return (
      <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center flex-shrink-0">
        <span className="text-xs font-medium text-gray-600">
          {getInitials(message.senderName)}
        </span>
      </div>
    )
  }

  return (
    <>
      <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} gap-2`}>
        {/* Avatar for incoming messages */}
        {!isOwn && <Avatar />}

        <div
          ref={bubbleRef}
          className={`max-w-[70%] relative ${
            isOwn
              ? 'bg-emerald-500 text-white rounded-l-2xl rounded-tr-2xl'
              : 'bg-gray-100 text-gray-900 rounded-r-2xl rounded-tl-2xl'
          } px-4 py-3 ${isOwn && onDelete ? 'cursor-pointer select-none' : ''}`}
          onContextMenu={handleContextMenu}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchMove={handleTouchEnd}
        >
          {/* Sender info for incoming messages */}
          {!isOwn && (
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-xs font-semibold text-gray-700">{message.senderName}</span>
              {(message.senderDepartment || message.senderCargo) && (
                <span className="text-xs px-1.5 py-0.5 bg-blue-100 rounded text-blue-600">
                  {message.senderDepartment || message.senderCargo}
                </span>
              )}
              <span className="text-xs px-1.5 py-0.5 bg-gray-200 rounded text-gray-500">
                {senderLabel}
              </span>
            </div>
          )}

          {/* Deleted message placeholder */}
          {isDeleted ? (
            <div className={`flex items-center gap-2 italic ${isOwn ? 'text-emerald-100' : 'text-gray-400'}`}>
              <Prohibit size={16} />
              <span className="text-sm">
                {isOwn ? 'Voce apagou esta ' : 'Esta '}{getDeletedMessageType(message)} foi apagada
              </span>
            </div>
          ) : (
            <>
              {/* Message content */}
              {message.content && (
                <p className={`text-sm whitespace-pre-wrap break-words ${isOwn ? 'text-white' : 'text-gray-800'}`}>
                  {message.content}
                </p>
              )}

              {/* Attachment */}
              {message.attachment && (
                <AttachmentPreview attachment={message.attachment} />
              )}
            </>
          )}

          {/* Time */}
          <div className={`flex justify-end mt-1 ${isOwn ? 'text-emerald-100' : 'text-gray-400'}`}>
            <span className="text-xs">{formatTime(message.createdAt)}</span>
          </div>

          {/* Context Menu */}
          {showContextMenu && isOwn && (
            <div
              className="fixed bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50"
              style={{
                top: contextMenuPosition.y || 'auto',
                left: contextMenuPosition.x || 'auto',
                ...(contextMenuPosition.x === 0 && contextMenuPosition.y === 0 && {
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)'
                })
              }}
            >
              <button
                onClick={handleDeleteClick}
                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <Trash size={16} />
                <span>Apagar mensagem</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteConfirm}
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        deleting={deleting}
      />
    </>
  )
}

export { formatDate }
